import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { thinkingModes } from '../data/modes';
import { studyPurposes } from '../data/purposes';
import { toolTypes } from '../data/toolTypes';
import { getDashboardRecommendation } from '../data/dashboardRecommendations';
import ScrollToTop from '../utils/ScrollToTop';

const {
  FiChevronRight, FiActivity, FiCpu, FiSliders, FiLayers,
  FiTarget, FiFileText, FiCheckSquare,
  FiGrid, FiMap, FiBox,
  FiVideo, FiMousePointer, FiFile, FiImage, FiMessageCircle,
  FiGitCommit, FiPlayCircle, FiMessageSquare,
} = FiIcons;

const BOARD_ICONS = {
  'Kanban-Board': FiGrid,
  'Lernlandkarte': FiMap,
  'Leitner-Box': FiBox,
};

const BOARD_COLORS = {
  'Kanban-Board': { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-300' },
  'Lernlandkarte': { bg: 'bg-violet-500/10', border: 'border-violet-500/30', text: 'text-violet-300' },
  'Leitner-Box': { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-300' },
};

const CARD_MAP = {
  'Lernkarte': { icon: FiFileText, color: 'violet' },
  'Bildkarte': { icon: FiImage, color: 'amber' },
  'Promptkarte': { icon: FiMessageSquare, color: 'emerald' },
  'Interaktives Arbeitsblatt': { icon: FiMousePointer, color: 'cyan' },
  'PDF-Dokument': { icon: FiFile, color: 'rose' },
};

const TOOL_MAP = {
  'Video': { icon: FiVideo, color: 'rose' },
  'GeoGebra': { icon: FiBox, color: 'blue' },
  'JS-Rechner': { icon: FiCpu, color: 'teal' },
  'JS-Animationen': { icon: FiPlayCircle, color: 'fuchsia' },
  'KI-Prompts': { icon: FiMessageCircle, color: 'emerald' },
  'ganze KI-Pfade': { icon: FiGitCommit, color: 'indigo' },
};

const PILL_COLORS = {
  violet: { bg: 'bg-violet-500/10', border: 'border-violet-500/30', text: 'text-violet-300' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-300' },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-300' },
  cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', text: 'text-cyan-300' },
  rose: { bg: 'bg-rose-500/10', border: 'border-rose-500/30', text: 'text-rose-300' },
  blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-300' },
  teal: { bg: 'bg-teal-500/10', border: 'border-teal-500/30', text: 'text-teal-300' },
  fuchsia: { bg: 'bg-fuchsia-500/10', border: 'border-fuchsia-500/30', text: 'text-fuchsia-300' },
  indigo: { bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', text: 'text-indigo-300' },
};

// Match card/tool string exactly or by substring
const getMapInfo = (str, map) => {
  if (map[str]) return map[str];
  for (const key of Object.keys(map)) {
    if (str.includes(key)) return map[key];
  }
  return { icon: FiCheckSquare, color: 'blue' };
};

export default function MissionDashboard({ modeId, purposeId, toolId, onNext }) {
  const mode = thinkingModes[modeId] || thinkingModes.structure;
  const purpose = studyPurposes[purposeId] || studyPurposes.sprint;
  const tool = toolTypes[toolId] || toolTypes.systematic;
  const rec = getDashboardRecommendation(modeId, toolId);
  const weeks = mode.getPlan ? mode.getPlan(purposeId) : [];

  const systemAdaptations = [
    {
      title: 'Goal-Kalibrierung',
      desc: `Dein 6-Wochen-Ziel wird auf '${purpose.title}' programmiert.`,
      icon: FiTarget,
      detail: purpose.intent,
    },
    {
      title: 'Task-Karten Modus',
      desc: `Layout: ${mode.title}-Ansicht aktiviert.`,
      icon: FiSliders,
      detail: `Fokus: ${mode.dlfStrategy}`,
    },
    {
      title: 'Content-Filter',
      desc: 'Inhalte werden nach Relevanz vorsortiert.',
      icon: FiLayers,
      detail: 'Nur erfolgskritische Themen.',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-6xl mx-auto pt-8 pb-20 px-4"
    >
      <ScrollToTop />

      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold mb-4 tracking-widest uppercase">
          <SafeIcon icon={FiCpu} />
          <span>System-Synchronisation</span>
        </div>
        <h2 className="text-5xl font-black text-white mb-6 tracking-tight">
          Deine System-Architektur
        </h2>
        <p className="text-slate-400 text-xl max-w-2xl mx-auto leading-relaxed">
          Der DigiLernFlow konfiguriert sich als{' '}
          <span className="text-blue-400 font-bold">{mode.title}</span>-Umgebung
          mit Tool-Stack{' '}
          <span className="text-emerald-400 font-bold">{tool.title}</span>.
        </p>
      </div>

      {/* System Adaptation Cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-14">
        {systemAdaptations.map((item, idx) => (
          <div
            key={idx}
            className="p-8 bg-slate-800/40 border border-slate-700/50 rounded-[32px] hover:border-blue-500/50 transition-all group"
          >
            <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-400 mb-6 group-hover:scale-110 transition-transform">
              <SafeIcon icon={item.icon} size={24} />
            </div>
            <h4 className="text-white font-bold mb-2 text-lg">{item.title}</h4>
            <p className="text-sm text-slate-400 leading-relaxed mb-4">{item.desc}</p>
            <div className="text-[10px] text-blue-400 font-black uppercase tracking-widest bg-blue-500/5 p-2 rounded-lg border border-blue-500/10">
              {item.detail}
            </div>
          </div>
        ))}
      </div>

      {/* Recommendation Block */}
      {rec && (
        <div className="mb-14">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px flex-1 bg-slate-800" />
            <h3 className="text-[12px] text-slate-400 font-black uppercase tracking-[0.2em]">
              Empfehlungen für deine Challenge
            </h3>
            <div className="h-px flex-1 bg-slate-800" />
          </div>

          <div className="space-y-6">
            {/* 1. Boards */}
            {rec.boards?.length > 0 && (
              <div>
                <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-3 flex items-center gap-2">
                  <span className="text-blue-400">1.</span> Empfohlene DigiLernFlow-Boards
                </div>
                <div className="flex flex-wrap gap-3">
                  {rec.boards.map((board, i) => {
                    const icon = BOARD_ICONS[board] || FiGrid;
                    const color = BOARD_COLORS[board] || BOARD_COLORS['Kanban-Board'];
                    return (
                      <div
                        key={i}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full border font-bold text-sm ${color.bg} ${color.border} ${color.text}`}
                      >
                        <SafeIcon icon={icon} size={14} />
                        {board}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 2. Card Types – clean labels, no parentheses */}
            {rec.cardTypes?.length > 0 && (
              <div>
                <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-3 flex items-center gap-2">
                  <span className="text-violet-400">2.</span> Bevorzugte Karten-Typen
                </div>
                <div className="flex flex-wrap gap-3">
                  {rec.cardTypes.map((card, i) => {
                    const mapInfo = getMapInfo(card, CARD_MAP);
                    const color = PILL_COLORS[mapInfo.color] || PILL_COLORS.blue;
                    return (
                      <div
                        key={i}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full border font-bold text-sm ${color.bg} ${color.border} ${color.text}`}
                      >
                        <SafeIcon icon={mapInfo.icon} size={14} />
                        {card}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 3. Tools */}
            {rec.tools?.length > 0 && (
              <div>
                <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-3 flex items-center gap-2">
                  <span className="text-emerald-400">3.</span> Integrierte Tools
                </div>
                <div className="flex flex-wrap gap-3">
                  {rec.tools.map((t, i) => {
                    const mapInfo = getMapInfo(t, TOOL_MAP);
                    const color = PILL_COLORS[mapInfo.color] || PILL_COLORS.blue;
                    return (
                      <div
                        key={i}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full border font-bold text-sm ${color.bg} ${color.border} ${color.text}`}
                      >
                        <SafeIcon icon={mapInfo.icon} size={14} />
                        {t}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 6-Wochen-Pfad */}
      <div className="bg-slate-900/40 border border-slate-800 rounded-[48px] p-8 md:p-12 mb-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/5 blur-3xl -mr-32 -mt-32" />
        <div className="flex items-center gap-4 mb-10 relative z-10">
          <div className="w-12 h-12 rounded-2xl bg-indigo-500 flex items-center justify-center text-white shadow-lg">
            <SafeIcon icon={FiActivity} size={24} />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-white uppercase tracking-tighter">
              Der 6-Wochen-Pfad
            </h3>
            <p className="text-slate-500 text-sm">Vorschau deiner individuellen Meilensteine</p>
          </div>
        </div>

        <div className="space-y-4 relative z-10">
          {weeks.slice(0, 3).map((week, idx) => (
            <div
              key={idx}
              className={`flex items-center gap-6 p-6 rounded-[32px] border ${
                idx === 0
                  ? 'bg-slate-800/80 border-blue-500/50'
                  : 'bg-slate-900/40 border-slate-800 opacity-60'
              }`}
            >
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black shrink-0 ${
                  idx === 0 ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-600'
                }`}
              >
                W{idx + 1}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-white font-bold text-sm">{week.title}</h4>
                <p className="text-xs text-slate-500">{week.focus}</p>
              </div>
              {idx === 0 && rec?.boards?.length > 0 && (
                <div className="ml-auto flex gap-2 flex-wrap justify-end shrink-0">
                  {rec.boards.slice(0, 2).map((b, i) => {
                    const color = BOARD_COLORS[b] || BOARD_COLORS['Kanban-Board'];
                    return (
                      <span
                        key={i}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${color.bg} ${color.border} ${color.text}`}
                      >
                        {b}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          {weeks.length > 3 && (
            <div className="text-center pt-4">
              <p className="text-xs text-slate-600 font-bold uppercase tracking-[0.2em]">
                … und {weeks.length - 3} weitere Meilensteine bis zur Meisterschaft
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-center">
        <button
          onClick={onNext}
          className="group px-12 py-7 bg-white text-indigo-900 font-black rounded-3xl text-2xl flex items-center gap-4 hover:bg-slate-100 transition-all shadow-xl active:scale-95"
        >
          Lernvertrag finalisieren
          <SafeIcon icon={FiChevronRight} className="group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </motion.div>
  );
}